import { FC } from 'react';

import 'assets/styles/PhoneNumber.css';
import { cva } from 'class-variance-authority';
import { BsInfoCircle } from 'react-icons/bs';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import type { UseMutateFunction } from '@tanstack/react-query';
import { Tooltip } from 'ui/components/common';
import { lowerCase } from 'lodash';

export interface PROPS {
  className?: string | string[];
  size?: SIZE;
  textType?: 'default' | 'danger' | 'success' | 'warning';
  name: string;
  label?: string;
  labelSubText?: string;
  subText?: string;
  required?: boolean;
  information?: string;
  defaultCountry?: string;
  disabled?: boolean;
  onChange: (name: string, value: string) => void;
  value?: string;
  placeholder?: string;
  onError?: (name: string, error: string) => void;
  onBlur?: (name: string, value: string) => void;
  validatePhoneNumber?: UseMutateFunction<any, unknown, string, unknown>;
}

const containerClasses = cva(
  ['border bg-fill-secondary rounded-[6px] w-full'],
  {
    variants: {
      textType: {
        default: ['border-separator-secondary'],
        danger: ['border-danger-base'],
        success: ['border-primary-base'],
        warning: ['border-warning-base'],
      },
      size: {
        [SIZE.Small]: ['max-h-9'],
        [SIZE.Medium]: ['max-h-[42px]'],
        [SIZE.Large]: ['max-h-[52px]'],
      },
    },
    defaultVariants: {
      textType: 'default',
      size: SIZE.Small,
    },
  },
);

const inputClasses = cva(
  ['!h-fit !bg-transparent !border-none px-2.5 !pl-[70px] max-w-full'],
  {
    variants: {
      size: {
        [SIZE.Small]: ['py-2 px-3', 'max-h-[36px]'],
        [SIZE.Medium]: ['p-3', 'max-h-[44px]'],
        [SIZE.Large]: ['px-[14px] py-[18px]', 'max-h-[52px]'],
      },
    },
    defaultVariants: {
      size: SIZE.Small,
    },
  },
);

const subTextClasses = cva(['text-xs'], {
  variants: {
    textType: {
      default: [],
      success: ['text-primary-base'],
      danger: ['text-danger-base'],
      warning: ['text-warning-base'],
    },
    size: {
      default: 'text-xs',
      [SIZE.Small]: 'text-xs',
      [SIZE.Medium]: 'text-xs',
      [SIZE.Large]: 'text-sm',
    },
  },
  defaultVariants: {
    textType: 'default',
  },
});

const PhoneNumber: FC<PROPS> = ({
  className,
  value,
  placeholder,
  size = SIZE.Small,
  textType,
  name,
  label,
  labelSubText = '(Optional)',
  subText,
  required = false,
  information,
  defaultCountry = lowerCase(import.meta.env.VITE_DEFAULT_COUNTRY),
  onChange,
  onError,
  onBlur,
  disabled,
  validatePhoneNumber,
}) => {
  return (
    <div className={cn(className, 'space-y-2.5')}>
      {(label || information) && (
        <div
          className={cn(
            'text-sm leading-[22px] flex items-center justify-between',
          )}
        >
          {label && (
            <label htmlFor={name}>
              <span className="mr-1 font-semibold">{label}</span>
              {required ? (
                <span className="text-danger-base">*</span>
              ) : (
                <span className="text-xs text-content">{labelSubText}</span>
              )}
            </label>
          )}
          {information && (
            <Tooltip title={information}>
              <div className="input-info">
                <BsInfoCircle className="text-content" />
              </div>
            </Tooltip>
          )}
        </div>
      )}
      <PhoneInput
        value={value}
        placeholder={placeholder}
        containerClass={cn(containerClasses({ textType, size }))}
        dropdownStyle={{ backgroundColor: 'white' }}
        dropdownClass="!bg-fill-secondary"
        inputClass={cn(inputClasses({ size }))}
        country={defaultCountry}
        enableSearch
        disableSearchIcon
        onChange={(val) => onChange(name, val ? `+${val}` : '')}
        onBlur={({ target: { value: val } }) => {
          if (val && validatePhoneNumber) {
            if (onBlur) onBlur(name, val);

            validatePhoneNumber(val, {
              onError: () => {
                if (onError) onError(name, 'Please enter a valid phone number');
              },
              onSuccess: ({ data }) => {
                if (!data.valid && onError)
                  onError(name, 'Please enter a valid phone number');
              },
            });
          } else {
            if (onBlur) onBlur(name, val);
          }
        }}
        disabled={disabled}
      />
      {subText && (
        <div className={cn(subTextClasses({ textType, size }))}>{subText}</div>
      )}
    </div>
  );
};

export default PhoneNumber;
